// Centralised browser/feature detection. Use this instead of scattered UA sniffs.
// Values are computed once at import and safe in SSR-style environments.

const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
const nav: any = typeof navigator !== "undefined" ? navigator : {};
const win: any = typeof window !== "undefined" ? window : {};

// --- Platform ---
export const isIOS =
  /iPad|iPhone|iPod/.test(ua) ||
  // iPadOS 13+ reports as Mac with touch support
  (nav.platform === "MacIntel" && (nav.maxTouchPoints ?? 0) > 1);

export const isAndroid = /Android/i.test(ua);
export const isMobile = isIOS || isAndroid;

// --- Browser engine ---
export const isSafari =
  /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
export const isFirefox = /firefox|fxios/i.test(ua);
export const isChrome = /chrome|crios/i.test(ua) && !/edg/i.test(ua);

// --- iOS Safari version (for push-notification gating: requires 16.4+) ---
function getIOSVersion(): { major: number; minor: number } | null {
  const m = ua.match(/OS (\d+)_(\d+)/);
  if (!m) return null;
  return { major: parseInt(m[1], 10), minor: parseInt(m[2], 10) };
}
const iosVer = getIOSVersion();
export const iosVersion = iosVer;
export const isIOS164OrLater =
  isIOS && !!iosVer && (iosVer.major > 16 || (iosVer.major === 16 && iosVer.minor >= 4));

// --- Display mode (PWA installed to home screen) ---
export const isStandalone = (() => {
  try {
    return (
      win.matchMedia?.("(display-mode: standalone)")?.matches ||
      nav.standalone === true // iOS Safari legacy flag
    );
  } catch {
    return false;
  }
})();

// --- Feature detection ---
export const canPushNotify = (() => {
  if (!("Notification" in win) || !("serviceWorker" in nav)) return false;
  // iOS only allows web push from an installed PWA on Safari 16.4+
  if (isIOS) return isIOS164OrLater && isStandalone;
  return true;
})();

// True if Chromium-style native install prompt (`beforeinstallprompt`) is supported.
// Firefox & Safari never fire it, show manual instructions instead.
export const canPromptInstall = "onbeforeinstallprompt" in win;

export const canInstallPWA = !isStandalone && (canPromptInstall || isIOS);

export const canRecordAudio =
  typeof win.MediaRecorder !== "undefined" &&
  !!nav.mediaDevices?.getUserMedia;

export const canShare = typeof nav.share === "function";
export const canWakeLock = "wakeLock" in nav;

// Convenience snapshot for logging / debug panels.
export const browserCapabilities = {
  isIOS,
  isAndroid,
  isMobile,
  isSafari,
  isFirefox,
  isChrome,
  iosVersion: iosVer,
  isIOS164OrLater,
  isStandalone,
  canPushNotify,
  canPromptInstall,
  canInstallPWA,
  canRecordAudio,
  canShare,
  canWakeLock,
};

/**
 * Why push isn't available, for user-facing copy.
 * Returns null when push IS available.
 */
export function pushUnavailableReason(): string | null {
  if (!("Notification" in win)) return "Your browser doesn't support notifications.";
  if (!("serviceWorker" in nav)) return "Your browser doesn't support service workers.";
  if (isIOS && !isIOS164OrLater)
    return "Push notifications require iOS 16.4 or later. Please update your device.";
  if (isIOS && !isStandalone)
    return "On iPhone/iPad, push notifications only work after you add this app to your Home Screen.";
  return null;
}